import React, { useState, useEffect } from 'react';
import { get5MatchService } from '../../services/get5MatchService';
import { teamService } from '../../services/teamService';
import './ServerSelectionModal.scss';

interface ServerSelectionModalProps {
  match: {
    challonge_id: string;
    team1: any;
    team2: any;
  };
  vetoPicks: Array<{ map: string }>;
  onClose: () => void;
  onConfirm: (serverId: number, team1Get5Id: number, team2Get5Id: number) => void;
}

interface Server {
  id: number;
  display_name: string;
  ip_string: string;
  port: number;
  in_use: boolean;
  public_server: boolean;
  name: string; // Owner name
  flag: string;
  gotv_port: number;
}

interface TeamMapping {
  ncsTeam: any;
  get5Id?: number;
  found: boolean;
}

const ServerSelectionModal: React.FC<ServerSelectionModalProps> = ({ 
  match, 
  vetoPicks, 
  onClose, 
  onConfirm 
}) => {
  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedServer, setSelectedServer] = useState<number | null>(null);
  const [teamMappings, setTeamMappings] = useState<{
    team1: TeamMapping;
    team2: TeamMapping;
  } | null>(null);
  const [spectatorInfo, setSpectatorInfo] = useState<{ count: number } | null>(null);

  useEffect(() => {
    loadData();
  }, [match.challonge_id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load servers, teams, and spectators in parallel
      const [serversResult, teamsResult, spectatorsResult] = await Promise.all([
        get5MatchService.getAvailableServers(match.challonge_id),
        teamService.getGet5Teams(),
        get5MatchService.getSpectators()
      ]);

      // Sort servers: available first, then in-use
      const sortedServers = [...serversResult.servers].sort((a, b) => {
        if (a.in_use === b.in_use) return 0;
        return a.in_use ? 1 : -1; // Available servers come first
      });
      
      setServers(sortedServers);
      setSpectatorInfo({ count: spectatorsResult.count });

      // Find team mappings
      const team1Mapping = findTeamMapping(match.team1, teamsResult);
      const team2Mapping = findTeamMapping(match.team2, teamsResult);

      setTeamMappings({
        team1: team1Mapping,
        team2: team2Mapping
      });

      // Pre-select first available server
      const firstAvailable = sortedServers.find(s => !s.in_use);
      if (firstAvailable) {
        setSelectedServer(firstAvailable.id);
      }
    } catch (err: any) {
      console.error('Failed to load data:', err);
      setError(err.message || 'Failed to load server data');
    } finally {
      setLoading(false);
    }
  };

  const findTeamMapping = (ncsTeam: any, get5Teams: any[]): TeamMapping => {
    // Try to find by name
    const found = get5Teams.find(t => 
      t.name.toLowerCase() === ncsTeam.teamName.toLowerCase()
    );

    if (found) {
      return {
        ncsTeam,
        get5Id: found.id,
        found: true
      };
    }

    return {
      ncsTeam,
      found: false
    };
  };

  const handleConfirm = () => {
    if (!selectedServer || !teamMappings) {
      return;
    }

    if (!teamMappings.team1.found || !teamMappings.team2.found) {
      setError('Teams must be synced to Get5 before creating a match');
      return;
    }

    onConfirm(
      selectedServer,
      teamMappings.team1.get5Id!,
      teamMappings.team2.get5Id!
    );
  };

  const canCreate = selectedServer && 
    teamMappings?.team1.found && 
    teamMappings?.team2.found;

  return (
    <div className="modal-overlay" onClick={onClose} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClose(); }}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} role="dialog" onKeyDown={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Get5 Match</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-spinner">Loading...</div>
          ) : error ? (
            <div className="alert alert-error">{error}</div>
          ) : (
            <>
              {/* Team Status */}
              <div className="section">
                <h3>Team Status</h3>
                {teamMappings && (
                  <div className="team-status">
                    <div className={`team-check ${teamMappings.team1.found ? 'found' : 'not-found'}`}>
                      <span className="team-name">{match.team1.teamName}</span>
                      <span className="status">
                        {teamMappings.team1.found ? '✓ Found in Get5' : '✗ Not found'}
                      </span>
                    </div>
                    <div className={`team-check ${teamMappings.team2.found ? 'found' : 'not-found'}`}>
                      <span className="team-name">{match.team2.teamName}</span>
                      <span className="status">
                        {teamMappings.team2.found ? '✓ Found in Get5' : '✗ Not found'}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Match Info */}
              <div className="section">
                <h3>Match Configuration</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Maps:</span>
                    <span className="value">{vetoPicks.map(p => p.map).join(', ')}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Best of:</span>
                    <span className="value">{vetoPicks.length}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Spectators:</span>
                    <span className="value">{spectatorInfo?.count || 0} observers/admins</span>
                  </div>
                </div>
              </div>

              {/* Server Selection */}
              <div className="section">
                <h3>Select Server</h3>
                {servers.length === 0 ? (
                  <div className="alert alert-warning">No servers available</div>
                ) : (
                  <div className="server-list">
                    {servers.map(server => (
                      <label key={server.id} className={`server-option ${server.in_use ? 'in-use' : 'available'}`}>
                        <input
                          type="radio"
                          name="server"
                          value={server.id}
                          checked={selectedServer === server.id}
                          onChange={(e) => setSelectedServer(Number(e.target.value))}
                          disabled={server.in_use}
                        />
                        <div className="server-info">
                          <div className="server-name">{server.display_name || `Server ${server.id}`}</div>
                          <div className="server-details">
                            {server.ip_string}:{server.port}
                            {server.name && <span className="owner">• Owner: {server.name}</span>}
                            <span className={`status-chip ${server.in_use ? 'in-use' : 'available'}`}>
                              {server.in_use ? 'In Use' : 'Available'}
                            </span>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Warning about team sync */}
              {teamMappings && (!teamMappings.team1.found || !teamMappings.team2.found) && (
                <div className="alert alert-warning">
                  Teams must be synced to Get5 before creating a match. 
                  Please sync teams in the admin panel first.
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="button button-secondary" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="button button-primary" 
            onClick={handleConfirm}
            disabled={!canCreate}
          >
            Create Match
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServerSelectionModal;